import { Stack } from '@common/components/Stack';
import { Routes } from '@common/constants';
import { useRoomSettings } from '@common/features/roomSettings';
import { useGetSpaSettings } from '@common/features/spa';
import { useTablesSettings } from '@common/features/tables/hooks/useGetTablesSettings';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useTranslations } from '@lang';
import { ArrowLeftIcon } from '@root/styles/icons';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { OrderSummary, OrderSummaryHeadings, OrderSummaryItems } from '../Account.styles';
import { AccountHeader } from '../components/AccountHeader';
import { AccountLayout } from '../components/AccountLayout';
import { CancelOrderForm } from '../components/CancelOrderForm';
import { NewCancelOrderForm } from '../components/NewCancelOrderForm';
import { allItemsCancellable } from '../helpers/allItemsCancellable.helper';
import { useGuestOrder } from '../queries';

type CancelOrderProps = {
  guestId: number;
  orderId: number;
  guestEmail: string;
  guestUuid: string;
  siteSlug: string;
  orderUuid: string;
  siteUuid: string;
  propertyId: number;
};

export const routeStates = {
  SUCCESS: 'success',
  CANCELLED: 'cancelled',
};

export function CancelOrder({
  guestId,
  orderId,
  guestEmail,
  guestUuid,
  siteSlug,
  orderUuid,
  siteUuid,
  propertyId,
}: CancelOrderProps) {
  const router = useRouter();
  const { t, formatDate, formatNumber } = useTranslations();
  const { data: order } = useGuestOrder({ guestId, orderId });
  const { data: rooms } = useRoomSettings();
  const { data: tables } = useTablesSettings();
  const { data: spa } = useGetSpaSettings(propertyId);

  const cancellationsEnabled = {
    spa: spa?.isCancellationsEnabled,
    rooms: rooms?.data?.cancellationsEnabled,
    tables: tables?.data?.cancellationsEnabled,
  };

  const areAllItemsCancellable = allItemsCancellable({
    order,
    cancellationsEnabled,
  });

  const isSuccess =
    router.query.state === routeStates.SUCCESS || router.query.state === routeStates.CANCELLED;

  const showNewCancellationForm = useFeatureIsOn('cancellations') && areAllItemsCancellable;

  if (isSuccess) {
    return (
      <>
        <AccountLayout>
          <AccountHeader>{t('orderRequest.heading')}</AccountHeader>

          <p>{t(routeStates.SUCCESS ? 'orderRequest.message' : 'orderRequest.cancelledMessage')}</p>

          <p>
            <Link href={Routes.HOME}>
              <a className="underline">{t('orderRequest.callToAction')}</a>
            </Link>
          </p>
        </AccountLayout>
      </>
    );
  }

  const tableHeadings = [
    t('myProfile.orderNumber'),
    t('myProfile.date'),
    t('myProfile.totalValue'),
    t('myProfile.status'),
  ];

  const orderSummaryFields = order
    ? [
        order.order_reference,
        formatDate(order.created_at, {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        }),
        order?.total?.amount
          ? formatNumber(order.total.amount / 100, {
              style: 'currency',
              currency: order.total.currency_code,
            })
          : '',
        order.order_status ? t(`myProfile.orderStatus.${order.order_status}`) : '',
      ]
    : [];
  return !!order ? (
    <>
      <AccountLayout>
        <AccountHeader>
          <Link href={Routes.ACCOUNT_ORDERS}>
            <a>
              <ArrowLeftIcon />
              {t('cancelOrder.heading')}
            </a>
          </Link>
        </AccountHeader>

        <Stack>
          <OrderSummary>
            <OrderSummaryHeadings>
              {tableHeadings.map((heading) => (
                <span>{heading}</span>
              ))}
            </OrderSummaryHeadings>
            <OrderSummaryItems>
              {orderSummaryFields.map((field) => (
                <span>{field}</span>
              ))}
            </OrderSummaryItems>
          </OrderSummary>

          {showNewCancellationForm ? (
            <NewCancelOrderForm
              data-testid="anon-modify-order-form"
              siteUuid={siteUuid}
              order={order}
              onOrderRequestSuccess={() =>
                router.replace({
                  query: {
                    ...router.query,
                    state: routeStates.CANCELLED,
                  },
                })
              }
            />
          ) : (
            <CancelOrderForm
              guestEmail={guestEmail}
              guestUuid={guestUuid}
              siteSlug={siteSlug}
              orderUuid={orderUuid}
              onOrderCancelled={() =>
                router.replace({
                  query: {
                    ...router.query,
                    state: routeStates.SUCCESS,
                  },
                })
              }
            />
          )}
        </Stack>
      </AccountLayout>
    </>
  ) : (
    <></>
  );
}
